import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useState,
  useEffect,
} from "react";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import { Modal, Button, CardMedia, Input, Slider } from "@mui/material";
import {
  useCreateToppingMutation,
  useUpdateToppingMutation,
  useUploadFileMutation,
  ToppingItem,
} from "../store/api/commonAPI";
import { showToast, showError } from "../constants/toasts";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  editTopping?: ToppingItem | null;
}

export default function AddTopping({
  open,
  onClose,
  setOpen,
  editTopping,
}: ModalProps) {
  const isEditMode = !!editTopping;
  const MAX_SLICE_IMAGES = 5;
  const [image, setImage] = useState<string>("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [sliceImageFile, setSliceImageFile] = useState<File[]>([]);
  const [sliceImage, setSliceImage] = useState<string[]>([]);
  const [fileName, setFileName] = useState<string>("");
  const [sliceFileName, setSliceFileName] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [quantityPerPiece, setQuantityPerPiece] = useState<string>("");
  const [maxLimit, setMaxLimit] = useState<string>("");
  const [errors, setErrors] = useState<{
    name?: string;
    image?: string;
    sliceImage?: string;
    quantityPerPiece?: string;
    maxLimit?: string;
  }>({});

  // Upload file and create/update topping mutations
  const [uploadFile, { isLoading: isUploading }] = useUploadFileMutation();
  const [createTopping, { isLoading: isCreating }] = useCreateToppingMutation();
  const [updateTopping, { isLoading: isUpdating }] = useUpdateToppingMutation();

  const isLoading = isUploading || isCreating || isUpdating;

  // State to track existing image URLs (for edit mode)
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string>("");
  const [uploadedSliceImageUrl, setUploadedSliceImageUrl] =
    useState<string>("");

  // Reset form when modal opens/closes or when editTopping changes
  useEffect(() => {
    if (!open) {
      setImage("");
      setImageFile(null);
      setSliceImage([]);
      setSliceImageFile([]);
      setFileName("");
      setSliceFileName("");
      setName("");
      setQuantityPerPiece("");
      setMaxLimit("");
      setUploadedImageUrl("");
      setUploadedSliceImageUrl("");
      setErrors({});
    } else if (editTopping) {
      // Populate form with edit data
      setName(editTopping.name || "");
      const existingImage = editTopping.image || "";
      const existingSliceImage = editTopping.sliceImage || "";
      // Format image URLs - handle both full URLs and relative paths
      const formattedImage = existingImage.startsWith("http")
        ? existingImage
        : existingImage
        ? `/static/images/${existingImage}`
        : "";
      const formattedSliceImage = existingSliceImage.startsWith("http")
        ? existingSliceImage
        : existingSliceImage
        ? `/static/images/${existingSliceImage}`
        : "";
      setImage(formattedImage);
      setUploadedImageUrl(existingImage); // Keep original URL for API
      setSliceImage([formattedSliceImage]);
      setUploadedSliceImageUrl(existingSliceImage); // Keep original URL for API
      setImageFile(null);
      setSliceImageFile([]);
      setFileName("");
      setSliceFileName("");
      setQuantityPerPiece(editTopping.quantityPerPiece?.toString() || "");
      setMaxLimit(editTopping.maxLimit?.toString() || "");
      setErrors({});
    } else {
      // Reset for add mode
      setImage("");
      setImageFile(null);
      setSliceImage([]);
      setSliceImageFile([]);
      setFileName("");
      setSliceFileName("");
      setName("");
      setQuantityPerPiece("");
      setMaxLimit("");
      setUploadedImageUrl("");
      setUploadedSliceImageUrl("");
      setErrors({});
    }
  }, [open, editTopping]);

  const [imageSizes, setImageSizes] = useState<{ [key: number]: number }>({});

  // 2. Update the handleSizeChange function to handle individual image sizes
  const handleSizeChange = (index: number, newValue: number | number[]) => {
    if (typeof newValue === "number") {
      setImageSizes((prev) => ({
        ...prev,
        [index]: newValue,
      }));
    }
  };

  const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = (event.target as HTMLInputElement).files;
    if (files && files[0]) {
      const file = files[0];

      // Validate file type
      if (!file.type.includes("image")) {
        setErrors((prev) => ({
          ...prev,
          image: "Please select a valid image file",
        }));
        return;
      }

      // Validate file size (e.g., max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({
          ...prev,
          image: "Image size should be less than 5MB",
        }));
        return;
      }

      setImageFile(file);
      setFileName(file.name);
      setImage(URL.createObjectURL(file));
      // Clear image error when user selects a new image
      if (errors.image) {
        setErrors((prev) => ({ ...prev, image: undefined }));
      }
    }
  };

  const handleSliceImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = (event.target as HTMLInputElement).files;
    if (!files || files.length === 0) return;

    // Calculate current total images (existing + new uploads)
    const currentTotalImages = sliceImage.length;
    const remainingSlots = MAX_SLICE_IMAGES - currentTotalImages;

    if (remainingSlots <= 0) {
      setErrors((prev) => ({
        ...prev,
        sliceImage: `Maximum ${MAX_SLICE_IMAGES} images allowed`,
      }));
      return;
    }

    const validFiles: File[] = [];
    const invalidFiles: string[] = [];

    // Process each file, but only up to the remaining slots
    Array.from(files).slice(0, remainingSlots).forEach((file) => {
      // Validate file type
      if (!file.type.includes("image")) {
        invalidFiles.push(`${file.name}: Not a valid image file`);
        return;
      }

      // Validate file size (e.g., max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        invalidFiles.push(`${file.name}: File size exceeds 5MB`);
        return;
      }

      validFiles.push(file);
    });

    // Check if user tried to upload more than remaining slots
    if (files.length > remainingSlots) {
      invalidFiles.push(
        `Only ${remainingSlots} more image(s) can be uploaded (maximum ${MAX_SLICE_IMAGES} total)`
      );
    }

    // If there are invalid files, show error for the first one
    if (invalidFiles.length > 0) {
      setErrors((prev) => ({ ...prev, sliceImage: invalidFiles[0] }));
      // Don't return here, we still want to process valid files
    } else if (errors.sliceImage) {
      // Clear any previous errors if all files are valid
      setErrors((prev) => ({ ...prev, sliceImage: undefined }));
    }

    // Process valid files
    if (validFiles.length > 0) {
      const newSliceImageFiles = [...(sliceImageFile || []), ...validFiles];
      const newSliceImages = validFiles.map((file) =>
        URL.createObjectURL(file)
      );

      setSliceImageFile(newSliceImageFiles);
      setSliceImage((prev) => [...(prev || []), ...newSliceImages]);
    }

    // Reset the input value to allow selecting the same file again
    event.target.value = "";
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setName(newName);
    if (errors.name) {
      setErrors((prev) => ({ ...prev, name: undefined }));
    }
  };

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow positive integers
    if (value === "" || /^\d+$/.test(value)) {
      setQuantityPerPiece(value);
      if (errors.quantityPerPiece) {
        setErrors((prev) => ({ ...prev, quantityPerPiece: undefined }));
      }
    }
  };

  const handleMaxLimitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow positive integers
    if (value === "" || /^\d+$/.test(value)) {
      setMaxLimit(value);
      if (errors.maxLimit) {
        setErrors((prev) => ({ ...prev, maxLimit: undefined }));
      }
    }
  };

  // Validate form fields
  const validateForm = (): boolean => {
    const newErrors: {
      name?: string;
      image?: string;
      sliceImage?: string;
      quantityPerPiece?: string;
      maxLimit?: string;
    } = {};

    // Validate name
    if (!name.trim()) {
      newErrors.name = "Category name is required";
    } else if (name.trim().length < 2) {
      newErrors.name = "Category name must be at least 2 characters";
    } else if (name.trim().length > 40) {
      newErrors.name = "Category name must be 40 characters or less";
    }

    // Validate image - required for add mode, optional for edit mode (if already has image)
    if (!isEditMode && !imageFile) {
      newErrors.image = "Please upload an image";
    } else if (isEditMode && !imageFile && !uploadedImageUrl) {
      newErrors.image = "Please upload an image";
    }

    // Validate slice image - can be empty string in edit mode
    if (!isEditMode && !sliceImageFile) {
      newErrors.sliceImage = "Please upload a slice image";
    } else if (sliceImage.length > MAX_SLICE_IMAGES) {
      newErrors.sliceImage = `Maximum ${MAX_SLICE_IMAGES} images allowed`;
    } else if (sliceImage.length === 0 && !uploadedSliceImageUrl) {
      newErrors.sliceImage = "Please upload at least one slice image";
    }

    // Validate quantity per piece
    if (!quantityPerPiece.trim()) {
      newErrors.quantityPerPiece = "Quantity per piece is required";
    } else {
      const quantity = parseInt(quantityPerPiece);
      if (isNaN(quantity) || quantity <= 0) {
        newErrors.quantityPerPiece = "Quantity must be a positive number";
      } else if (quantity > 1000) {
        newErrors.quantityPerPiece = "Quantity must be less than 1000";
      }
    }

    // Validate max limit
    if (!maxLimit.trim()) {
      newErrors.maxLimit = "Max limit is required";
    } else {
      const limit = parseInt(maxLimit);
      if (isNaN(limit) || limit <= 0) {
        newErrors.maxLimit = "Max limit must be a positive number";
      } else if (limit > 10000) {
        newErrors.maxLimit = "Max limit must be less than 10000";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      showError("Please fix the errors in the form");
      return;
    }

    try {
      let imageUrl = uploadedImageUrl;
      let sliceImageUrl = uploadedSliceImageUrl;

      // Step 1: Upload main image if a new file is selected
      if (imageFile) {
        const uploadResult = await uploadFile({ file: imageFile }).unwrap();

        // Extract image URL from upload response (same pattern as profile page)
        imageUrl = uploadResult.data?.data || uploadResult.data;

        if (!imageUrl) {
          showError("Failed to get image URL from server.");
          return;
        }
      }

      // If no image URL available, show error
      if (!imageUrl) {
        showError("Please upload an image");
        return;
      }

      // Step 2: Upload slice images if new files are selected
      let sliceImageUrls: string[] = [];

      if (sliceImageFile && sliceImageFile.length > 0) {
        // If we have existing uploaded slice images, include them
        if (uploadedSliceImageUrl) {
          sliceImageUrls = [
            ...sliceImageUrls,
            ...uploadedSliceImageUrl.split(","),
          ];
        }

        // Upload all new slice images
        for (const file of sliceImageFile) {
          if (file) {
            try {
              const sliceUploadResult = await uploadFile({
                file: file as File,
              }).unwrap();

              const uploadedUrl =
                sliceUploadResult.data?.data || sliceUploadResult.data;
              if (uploadedUrl) {
                sliceImageUrls.push(uploadedUrl);
              }
            } catch (error) {
              console.error("Error uploading slice image:", error);
              // Continue with other uploads even if one fails
            }
          }
        }

        // Join all slice image URLs with comma for the API
        sliceImageUrl = sliceImageUrls.join(",");
      } else if (uploadedSliceImageUrl) {
        // If no new files but we have existing URLs, use them
        sliceImageUrl = uploadedSliceImageUrl;
      }

      // Step 3: Create or update topping with image URLs
      // if (isEditMode && editTopping?._id) {
      //   // Update existing topping
      //   const updateResponse = await updateTopping({
      //     id: editTopping._id,
      //     name: name.trim(),
      //     image: imageUrl,
      //     sliceImage: sliceImageUrl || "",
      //     quantityPerPiece: parseInt(quantityPerPiece),
      //     maxLimit: parseInt(maxLimit),
      //   }).unwrap();

      //   // Show success message from API response or default message
      //   const successMessage =
      //     updateResponse?.message ||
      //     `Topping "${name.trim()}" updated successfully`;
      //   showToast(successMessage);
      // } else {
      //   // Create new topping
      //   const createResponse = await createTopping({
      //     name: name.trim(),
      //     image: imageUrl,
      //     sliceImage: sliceImageUrls.map((url) => ({
      //       image: url,
      //       multiplier: "1.0",
      //     })),
      //     quantityPerPiece: parseInt(quantityPerPiece),
      //     maxLimit: parseInt(maxLimit),
      //   }).unwrap();

      //   // Show success message from API response or default message
      //   const successMessage =
      //     createResponse?.message ||
      //     `Topping "${name.trim()}" created successfully`;
      //   showToast(successMessage);
      // }

      // Reset form and close modal
      setImage("");
      setImageFile(null);
      setSliceImage([]);
      setSliceImageFile([]);
      setFileName("");
      setSliceFileName("");
      setName("");
      setQuantityPerPiece("");
      setMaxLimit("");
      setUploadedImageUrl("");
      setUploadedSliceImageUrl("");
      setErrors({});
      setOpen(false);
    } catch (error: any) {
      console.error(
        `Error ${isEditMode ? "updating" : "creating"} topping:`,
        error
      );

      // Extract error message from API response or show default
      const errorMessage =
        error?.data?.message ||
        error?.error?.data?.message ||
        error?.message ||
        `Failed to ${
          isEditMode ? "update" : "create"
        } topping. Please try again.`;

      showError(errorMessage);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setOpen(false);
    }
  };

  return (
    <Modal
      className="modal rejectReason_modal"
      open={open}
      onClose={handleClose}
    >
      <div className="modal-dialog">
        <div className="modal-body">
          <div className="modal_title">
            <h2>{isEditMode ? "Edit Topping" : "Add Topping"}</h2>
            <button
              type="button"
              className="btn-close"
              onClick={handleClose}
              aria-label="Close"
              disabled={isLoading}
            >
              <CloseIcon />
            </button>
          </div>

          <form className="form" onSubmit={handleSubmit}>
            <div className="gap_p">
              <div className="control_group w_50">
                <label htmlFor="icon-button-file">
                  Upload Image<span className="req">*</span>
                </label>
                {image || uploadedImageUrl ? (
                  <div className="upload_image">
                    <div className="upload_image_holder no_radius v2">
                      <figure>
                        <CardMedia
                          component="img"
                          image={image || uploadedImageUrl}
                          alt="photo"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src =
                              "/static/images/placeholder.png";
                          }}
                        />
                      </figure>
                      <CloseIcon
                        onClick={() => {
                          if (!isLoading) {
                            setImage("");
                            setImageFile(null);
                            setUploadedImageUrl("");
                            setFileName("");
                            if (errors.image) {
                              setErrors((prev) => ({
                                ...prev,
                                image: undefined,
                              }));
                            }
                          }
                        }}
                        style={{
                          cursor: isLoading ? "not-allowed" : "pointer",
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <label className="upload_image" htmlFor="icon-button-file">
                    <Input
                      sx={{ display: "none" }}
                      id="icon-button-file"
                      type="file"
                      inputProps={{ accept: "image/png,image/jpeg,image/jpg" }}
                      onChange={handleImageChange}
                      disabled={isLoading}
                    />
                    <span className="upload_image_holder no_radius">
                      <figure className="add_img">
                        <img src="/static/images/placeholder.png" alt="icon" />
                        <span className="upload_img_icon">
                          <img src="/static/images/edit2_icon.svg" alt="icon" />
                        </span>
                      </figure>
                    </span>
                  </label>
                )}
                {errors.image && (
                  <div
                    style={{
                      color: "#d32f2f",
                      fontSize: "0.75rem",
                      marginTop: "4px",
                    }}
                  >
                    {errors.image}
                  </div>
                )}
              </div>
              <div className="control_group w_50">
                <label htmlFor="slice-icon-button-file">
                  Upload Slice Image<span className="req">*</span>
                  {sliceImage.length > 0 && (
                    <span style={{ marginLeft: "8px", fontSize: "0.875rem", color: "#666", fontWeight: "normal" }}>
                      ({sliceImage.length} / {MAX_SLICE_IMAGES})
                    </span>
                  )}
                </label>
                <div
                  className="uploaded-images-container"
                  style={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "flex-start" }}
                >
                  {sliceImage.length > 0 || uploadedSliceImageUrl ? (
                    sliceImage.map((img, index) => {
                      const currentSize = imageSizes[index] || 1;
                      return (
                        <div
                          key={index}
                          className="image-container"
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: "8px",
                            padding: "12px",
                            backgroundColor: "#f5f5f5",
                            borderRadius: "8px",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                            width: "180px",
                          }}
                        >
                          <div
                            className="upload_image_holder no_radius v2"
                            style={{
                              position: "relative",
                              width: "150px",
                              height: "150px",
                              overflow: "hidden",
                              borderRadius: "8px",
                              border: "1px solid #e0e0e0",
                            }}
                          >
                            <CardMedia
                              component="img"
                              image={img}
                              alt={`slice-photo-${index}`}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                transform: `scale(${currentSize})`,
                                transformOrigin: "center center",
                                transition: "transform 0.2s ease-in-out",
                              }}
                              onError={(e) => {
                                (e.target as HTMLImageElement).src =
                                  "/static/images/placeholder.png";
                              }}
                            />
                            <CloseIcon
                              onClick={() => {
                                if (!isLoading) {
                                  const newSliceImages = [...sliceImage];
                                  const newSliceFiles = [...sliceImageFile];
                                  newSliceImages.splice(index, 1);
                                  newSliceFiles.splice(index, 1);
                                  setSliceImage(newSliceImages);
                                  setSliceImageFile(newSliceFiles);
                                  // Remove the size from state when image is removed
                                  const newSizes = { ...imageSizes };
                                  delete newSizes[index];
                                  // Reindex the sizes
                                  const reindexedSizes: { [key: number]: number } = {};
                                  Object.keys(newSizes).forEach((key) => {
                                    const oldIndex = parseInt(key);
                                    if (oldIndex > index) {
                                      reindexedSizes[oldIndex - 1] = newSizes[oldIndex];
                                    } else if (oldIndex < index) {
                                      reindexedSizes[oldIndex] = newSizes[oldIndex];
                                    }
                                  });
                                  setImageSizes(reindexedSizes);
                                  // Clear error if it was about max images
                                  if (errors.sliceImage?.includes("Maximum")) {
                                    setErrors((prev) => ({ ...prev, sliceImage: undefined }));
                                  }
                                }
                              }}
                              style={{
                                position: "absolute",
                                top: "5px",
                                right: "5px",
                                background: "rgba(255, 255, 255, 0.8)",
                                borderRadius: "50%",
                                padding: "4px",
                                cursor: "pointer",
                                zIndex: 1,
                                // "&:hover": {
                                //   background: "rgba(255, 255, 255, 1)",
                                // },
                              }}
                            />
                          </div>

                          <div
                            className="size-control"
                            style={{
                              width: "100%",
                              padding: "0 8px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                marginBottom: "4px",
                                fontSize: "0.75rem",
                                color: "#666",
                              }}
                            >
                              <span>Size: {currentSize.toFixed(1)}x</span>
                            </div>
                            <Slider
                              value={currentSize}
                              onChange={(e, val) =>
                                handleSizeChange(index, val)
                              }
                              min={0.1}
                              max={2}
                              step={0.1}
                              size="small"
                              valueLabelDisplay="auto"
                              valueLabelFormat={(value) =>
                                `${value.toFixed(1)}x`
                              }
                              sx={{
                                color: "#1976d2",
                                "& .MuiSlider-thumb": {
                                  width: 16,
                                  height: 16,
                                  "&:hover, &.Mui-focusVisible": {
                                    boxShadow:
                                      "0 0 0 8px rgba(25, 118, 210, 0.16)",
                                  },
                                },
                                "& .MuiSlider-rail": {
                                  opacity: 0.5,
                                  backgroundColor: "#bfbfbf",
                                },
                                "& .MuiSlider-track": {
                                  border: "none",
                                },
                              }}
                            />
                          </div>
                        </div>
                      );
                    })
                  ) : null}
                  {/* Show upload container only if less than 5 images - appears on the right side */}
                  {sliceImage.length < MAX_SLICE_IMAGES && (
                    <label
                      className="upload_image"
                      htmlFor="slice-icon-button-file"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "180px",
                        height: "150px",
                        cursor: isLoading || sliceImage.length >= MAX_SLICE_IMAGES ? "not-allowed" : "pointer",
                        opacity: isLoading || sliceImage.length >= MAX_SLICE_IMAGES ? 0.6 : 1,
                      }}
                    >
                      <Input
                        sx={{ display: "none" }}
                        id="slice-icon-button-file"
                        type="file"
                        inputProps={{
                          accept: "image/png,image/jpeg,image/jpg",
                          multiple: true,
                        }}
                        onChange={handleSliceImageChange}
                        disabled={isLoading || sliceImage.length >= MAX_SLICE_IMAGES}
                      />
                      <span className="upload_image_holder no_radius">
                        <figure className="add_img">
                          <img src="/static/images/placeholder.png" alt="icon" />
                          <span className="upload_img_icon">
                            <img src="/static/images/edit2_icon.svg" alt="icon" />
                          </span>
                        </figure>
                      </span>
                    </label>
                  )}
                </div>
                {/* <div className="control_group w_50">
                  <label htmlFor="Size">Size</label>
                  <Slider
                    defaultValue={1}
                    aria-label="Size"
                    valueLabelDisplay="auto"
                    step={0.1}
                    min={1}
                    max={3}
                    marks
                    value={size || 1}
                    onChange={handleSizeChange}
                    disabled={isLoading}
                  />
                </div> */}
              </div>
              <div className="control_group w_100">
                <label htmlFor="CategoryName">
                  Category Name <span className="req">*</span>
                </label>
                <TextField
                  id="CategoryName"
                  placeholder="Enter Name"
                  fullWidth
                  hiddenLabel
                  value={name}
                  onChange={handleNameChange}
                  error={!!errors.name}
                  helperText={errors.name || " "}
                  disabled={isLoading}
                  required
                  inputProps={{ maxLength: 40 }}
                  FormHelperTextProps={{
                    style: { margin: 0, minHeight: errors.name ? "auto" : "20px" }
                  }}
                />
              </div>
              <div className="control_group w_50">
                <label htmlFor="Quantity">
                  Quantity per piece <span className="req">*</span>
                </label>
                <TextField
                  id="Quantity"
                  placeholder="Enter piece quantity"
                  fullWidth
                  hiddenLabel
                  type="text"
                  inputMode="numeric"
                  value={quantityPerPiece}
                  onChange={handleQuantityChange}
                  error={!!errors.quantityPerPiece}
                  helperText={errors.quantityPerPiece}
                  disabled={isLoading}
                  required
                />
              </div>
              <div className="control_group w_50">
                <label htmlFor="limit">
                  Set Max Limit <span className="req">*</span>
                </label>
                <TextField
                  id="limit"
                  placeholder="Enter limit number"
                  fullWidth
                  hiddenLabel
                  type="text"
                  inputMode="numeric"
                  value={maxLimit}
                  onChange={handleMaxLimitChange}
                  error={!!errors.maxLimit}
                  helperText={errors.maxLimit}
                  disabled={isLoading}
                  required
                />
              </div>
            </div>
            <div className="footer_btn_flex">
              <Button
                className="btn btn_grey"
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="btn btn_primary"
                disabled={isLoading}
                sx={{
                  color: isLoading ? "#ffffff" : undefined,
                  "&:disabled": {
                    color: "#ffffff",
                  },
                }}
              >
                {isLoading
                  ? isUploading
                    ? "Uploading..."
                    : isEditMode
                    ? "Updating..."
                    : "Creating..."
                  : isEditMode
                  ? "Update Topping"
                  : "Save"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
}
